import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getBlogs, getBlogsCount, getDraftBlogsCount, getPublishedBlogsCount } from "../../services/servicesblogs";
import "./Dashboard.css";

// Accept several possible response shapes from the blog service.
const extractBlogs = (response) => {
  if (Array.isArray(response?.data?.blogs)) return response.data.blogs;
  if (Array.isArray(response?.blogs)) return response.blogs;
  if (Array.isArray(response?.data?.data?.blogs)) return response.data.data.blogs;
  return [];
};

// Dashboard gives a quick summary of content activity.
export default function Dashboard({ onCreatePost }) {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [counts, setCounts] = useState(null);
  const [publishedCount, setPublishedCount] = useState(null);
  const [draftCount, setDraftCount] = useState(null);
  const handleCreatePost =
    onCreatePost || (() => navigate("/ContentManager/ContentManagerLayout/blogposts/create"));

  useEffect(() => {
    // Loads all dashboard data in parallel so the page can render faster.
    const loadDashboard = async () => {
      try {
        const [blogsResponse, countResponse, publishedCountResponse, draftCountResponse] = await Promise.allSettled([
          getBlogs(),
          getBlogsCount(),
          getPublishedBlogsCount(),
          getDraftBlogsCount(),
        ]);

        if (blogsResponse.status === "fulfilled") {
          setBlogs(extractBlogs(blogsResponse.value));
        } else {
          console.error("Error fetching dashboard blogs:", blogsResponse.reason);
          setBlogs([]);
        }

        if (countResponse.status === "fulfilled") {
          const payload = countResponse.value;
          const data = payload?.data ?? payload;
          setCounts({
            total:
              data?.total ??
              data?.count ??
              data?.all ??
              data?.totalBlogs ??
              null,
            published:
              data?.published ??
              data?.publishedCount ??
              data?.publishedBlogs ??
              null,
            drafts:
              data?.drafts ??
              data?.draftCount ??
              data?.draft ??
              data?.draftBlogs ??
              null,
          });
        } else {
          setCounts(null);
        }

        if (publishedCountResponse.status === "fulfilled") {
          const payload = publishedCountResponse.value;
          const data = payload?.data ?? payload;
          setPublishedCount(
            data?.published ??
            data?.count ??
            data?.total ??
            data?.publishedCount ??
            data?.publishedBlogs ??
            null
          );
        } else {
          setPublishedCount(null);
        }

        if (draftCountResponse.status === "fulfilled") {
          const payload = draftCountResponse.value;
          const data = payload?.data ?? payload;
          setDraftCount(
            data?.drafts ??
            data?.count ??
            data?.total ??
            data?.draftCount ??
            data?.draftBlogs ??
            null
          );
        } else {
          setDraftCount(null);
        }
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
        setBlogs([]);
        setCounts(null);
        setPublishedCount(null);
        setDraftCount(null);
      }
    };
    loadDashboard();
  }, []);

  const stats = useMemo(() => {
    const published = blogs.filter((blog) => String(blog.status).toUpperCase() === "PUBLISHED").length;
    return {
      total: counts?.total ?? blogs.length,
      published: publishedCount ?? counts?.published ?? published,
      drafts: draftCount ?? counts?.drafts ?? (blogs.length - published),
      images: blogs.filter((blog) => Boolean(blog.featured_image)).length,
    };
  }, [blogs, counts, publishedCount, draftCount]);

  const recentBlogs = useMemo(
    () =>
      blogs.slice(0, 5).map((blog) => ({
        id: blog.id || blog._id,
        title: blog.title,
        updated: blog.updatedAt ? new Date(blog.updatedAt).toLocaleDateString("en-GB") : "--",
        status: String(blog.status).toUpperCase() === "PUBLISHED",
      })),
    [blogs]
  );

  return (
    <>
      <div className="cm-top">
        <div>
          <h1>Dashboard</h1>
          <p className="page-subtitle">Welcome back, Content Manager</p>
        </div>

        <button type="button" className="primary-btn" onClick={handleCreatePost}>
          + New Post
        </button>
      </div>

      <section className="cm-cards">
        <article className="cm-card">
          <div className="cm-card-head">
            <span>Total Posts</span>
          </div>
          <h2>{stats.total}</h2>
          <p>All blog posts</p>
        </article>

        <article className="cm-card">
          <div className="cm-card-head">
            <span>Published</span>
          </div>
          <h2>{stats.published}</h2>
          <p>Live on website</p>
        </article>

        <article className="cm-card">
          <div className="cm-card-head">
            <span>Drafts</span>
          </div>
          <h2>{stats.drafts}</h2>
          <p>Not yet published</p>
        </article>

      </section>

      <section className="recent-posts">
        <h3>Recent Posts</h3>

        {recentBlogs.map((blog) => (
          <div key={blog.id} className="recent-item">
            <div>
              <strong>{blog.title}</strong>
              <p>Updated {blog.updated}</p>
            </div>

            <span className={`badge ${blog.status ? "published" : "draft"}`}>
              {blog.status ? "published" : "draft"}
            </span>
          </div>
        ))}
      </section>
    </>
  );
}
